/**
 * Input schemas for the Super Admin authoring API (Programme → Track → Module →
 * LearningItem). Every field is validated here before it reaches the database.
 * Allowed string-enum values are duplicated from the Prisma comments because the
 * SQL Server connector has no native enums.
 */
import { z } from "zod";
export const CONTENT_STATUSES = ["DRAFT", "PUBLISHED"];
export const TRACK_FLOWS = ["SEQUENTIAL", "FLEXIBLE"];
export const LEARNING_ITEM_TYPES = [
    "HTML_PACKAGE",
    "DOWNLOADABLE_RESOURCE",
    "EXTERNAL_LINK",
];
// Assessment-related enums (ON_FINAL_FAILURE, SELECTION_MODES, QUESTION_TYPES)
// live in ./assessment.ts, their real home now that the engine is built.
const title = z.string().trim().min(1).max(200);
// Learning item titles double as the section's stated objective and may run
// several lines (Anne, 2026-09-11) — matches the LearningItem.title column
// (NVARCHAR(1000)), unlike the short single-line title used elsewhere.
const learningItemTitle = z.string().trim().min(1).max(1000);
const order = z.number().int().min(0).max(10_000);
// --- Programme --------------------------------------------------------------
export const createProgrammeSchema = z.object({
    title,
    description: z.string().max(4000).optional(),
});
export const updateProgrammeSchema = createProgrammeSchema.partial();
// --- Track ------------------------------------------------------------------
export const createTrackSchema = z.object({
    programmeId: z.string().min(1),
    title,
    order,
    flow: z.enum(TRACK_FLOWS).default("SEQUENTIAL"),
    passThresholdPercent: z.number().int().min(0).max(100).default(70),
});
export const updateTrackSchema = createTrackSchema.omit({ programmeId: true }).partial();
export const setPrerequisitesSchema = z.object({
    requiredTrackIds: z.array(z.string().min(1)).max(20),
});
// --- Module ----------------------------------------------------------------
export const createModuleSchema = z.object({
    trackId: z.string().min(1),
    title,
    order,
    required: z.boolean().default(true),
});
export const updateModuleSchema = createModuleSchema.omit({ trackId: true }).partial();
// --- Learning item -------------------------------------------------------
export const createLearningItemSchema = z
    .object({
    moduleId: z.string().min(1),
    type: z.enum(LEARNING_ITEM_TYPES),
    title: learningItemTitle,
    order,
    downloadAllowed: z.boolean().default(false),
    externalUrl: z.string().url().max(2048).optional(),
})
    .refine((v) => v.type !== "EXTERNAL_LINK" || !!v.externalUrl, {
    message: "externalUrl is required for EXTERNAL_LINK items",
    path: ["externalUrl"],
});
export const updateLearningItemSchema = z
    .object({
    title: learningItemTitle.optional(),
    order: order.optional(),
    downloadAllowed: z.boolean().optional(),
    externalUrl: z.string().url().max(2048).optional(),
})
    .strict();
// --- Publish / unpublish -------------------------------------------------
export const PUBLISHABLE_ENTITIES = ["programme", "track", "module", "learningItem"];
export const publishParamsSchema = z.object({
    entity: z.enum(PUBLISHABLE_ENTITIES),
    id: z.string().min(1),
});
//# sourceMappingURL=authoring.js.map